import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  Box,
} from "@material-ui/core";
import LoadingButton from "@material-ui/lab/LoadingButton";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { facturasApi } from "src/services/facturasApi";
import { useSnackbar } from "src/components/snackbar";

export default function ValidacionPagoDialog({
  open,
  onClose,
  factura,
  tarea,
}) {
  const [comentarioValidacion, setComentarioValidacion] = useState("");
  const [aprobacionSeleccionada, setAprobacionSeleccionada] = useState(true);

  const [validarPago, { isLoading: validandoPago }] =
    facturasApi.useValidatePaymentSupervisorMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = useCallback(() => {
    setComentarioValidacion("");
    setAprobacionSeleccionada(true);
    onClose();
  }, [onClose]);

  const procesarValidacion = useCallback(async () => {
    try {
      await validarPago({
        idFactura: factura.id,
        aprobado: aprobacionSeleccionada,
        comentario: comentarioValidacion,
        refTarea: tarea.id,
      }).unwrap();

      enqueueSnackbar("¡Factura aprobada para pago correctamente!", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });
      handleClose();
    } catch (error) {
      console.error("Error al validar pago:", error);
      enqueueSnackbar(
        error?.data?.message ||
          error.message ||
          "Ocurrió un error al validar pago",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    }
  }, [
    aprobacionSeleccionada,
    comentarioValidacion,
    enqueueSnackbar,
    factura?.id,
    tarea?.id,
    handleClose,
    validarPago,
  ]);

  if (!factura) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {aprobacionSeleccionada ? "Aprobar" : "Rechazar"} Pago de Factura
      </DialogTitle>

      <DialogContent>
        {/* Información de la factura */}
        <Box mb={2}>
          <Typography variant="subtitle2" color="textSecondary">
            Información de la factura:
          </Typography>
          <Typography variant="body2">
            <strong>Folio:</strong> {factura.folio}
          </Typography>
          <Typography variant="body2">
            <strong>Total:</strong> ${factura.total?.toLocaleString("es-CL")}
          </Typography>
          <Typography variant="body2">
            <strong>Empleado:</strong> {factura.empleado}
          </Typography>
          <Typography variant="body2">
            <strong>Tarea:</strong> {tarea.nombre}
          </Typography>
        </Box>

        {/* Switch de aprobación/rechazo */}
        <FormControlLabel
          control={
            <Switch
              checked={aprobacionSeleccionada}
              onChange={(e) => setAprobacionSeleccionada(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body1" fontWeight="medium">
              {aprobacionSeleccionada ? "Aprobar pago" : "Rechazar pago"}
            </Typography>
          }
        />

        {/* Campo de comentario */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Comentario"
          value={comentarioValidacion}
          onChange={(e) => setComentarioValidacion(e.target.value)}
          placeholder={
            aprobacionSeleccionada
              ? "Trabajo completado satisfactoriamente. El empleado puede recibir su pago..."
              : "Motivo del rechazo. Especifique qué aspectos del trabajo no fueron completados correctamente..."
          }
          sx={{ mt: 2 }}
          helperText={
            aprobacionSeleccionada
              ? "Opcional: Agregue comentarios sobre el trabajo realizado"
              : "Requerido: Especifique el motivo del rechazo"
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={validandoPago}>
          Cancelar
        </Button>
        <LoadingButton
          loading={validandoPago}
          onClick={procesarValidacion}
          color={aprobacionSeleccionada ? "success" : "error"}
          variant="contained"
          startIcon={aprobacionSeleccionada ? <CheckIcon /> : <CloseIcon />}
          disabled={!aprobacionSeleccionada && !comentarioValidacion.trim()}
        >
          {aprobacionSeleccionada ? "Aprobar Pago" : "Rechazar Pago"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
